import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isUserLoggedIn } from "../utils/cookie-utils";

const educationLinks = [
    ["Investor FAQ", "/faq/public/investors"],
    ["Entrepreneur FAQ", "/faq/public/entrepreneurs"],
    ["Resources", "/resources"],
    ["Glossary", "/faq/public/glossary"],
];

const getInitials = (user) => {
    if(!user) return "U";

    const initials = `${user.first_name?.charAt(0) || ""}${
        user.last_name?.charAt(0) || ""
    }`.toUpperCase();

    return initials || user.email?.charAt(0).toUpperCase() || "U";
};

const TopBar = () => {
    const navigate = useNavigate();
    const publicUrl = process.env.REACT_APP_FRONTEND_PUBLIC_URL || "";
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || "";
    const educationRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const mobileMenuButtonRef = useRef(null);
    const [user, setUser] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [canAccessDashboard, setCanAccessDashboard] = useState(false);
    const [showProfilePicture, setShowProfilePicture] = useState(false);
    const [hasGeneralFAQ, setHasGeneralFAQ] = useState(false);
    const [showEducationDropdown, setShowEducationDropdown] = useState(false);
    const [showMobileEducationDropdown, setShowMobileEducationDropdown] =
        useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const fetchUser = async() => {
            try {
                if(!(await isUserLoggedIn())) return;

                const userResponse = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
                    { method: "GET", credentials: "include" },
                );
                const userData = await userResponse.json();

                if(!userData?.id) return;
                setUser(userData);

                const accessResponse = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/dashboard-access`,
                    { method: "GET", credentials: "include" },
                );
                const accessData = await accessResponse.json();

                setCanAccessDashboard(Boolean(accessData.canAccessDashboard));
                setShowProfilePicture(Boolean(accessData.showProfilePicture));

                if(accessData.showProfilePicture) {
                    const profileResponse = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/profile?user_id=${userData.id}`,
                        { method: "GET", credentials: "include" },
                    );
                    const profileData = await profileResponse.json();

                    if(profileData?.profileImage) {
                        setProfileImage(
                            `${process.env.REACT_APP_BACKEND_URL}/${profileData.profileImage}`,
                        );
                    }
                }
            } catch(error) {
                if(error.name !== "TypeError" && error.message !== "Failed to fetch") {
                    console.error("Topbar user fetch error:", error);
                }
                setUser(null);
                setProfileImage(null);
                setCanAccessDashboard(false);
                setShowProfilePicture(false);
            }
        };

        fetchUser();
    }, []);

    useEffect(() => {
        const fetchGeneralFAQ = async() => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/faq/public/general`,
                );
                const data = await response.json();
                setHasGeneralFAQ(Array.isArray(data) && data.length > 0);
            } catch{
                setHasGeneralFAQ(false);
            }
        };

        fetchGeneralFAQ();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if(
                educationRef.current &&
                !educationRef.current.contains(event.target)
            ) {
                setShowEducationDropdown(false);
            }

            if(
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(event.target) &&
                mobileMenuButtonRef.current &&
                !mobileMenuButtonRef.current.contains(event.target)
            ) {
                setIsMobileMenuOpen(false);
                setShowMobileEducationDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
        setShowEducationDropdown(false);
        setShowMobileEducationDropdown(false);
    };

    const goTo = (path) => {
        closeMobileMenu();
        navigate(path);
    };

    const goToPublic = (path) => {
        closeMobileMenu();
        window.location.href = `${publicUrl}${path}`;
    };

    const goToFrontend = (path) => {
        closeMobileMenu();
        window.location.href = `${frontendUrl}${path}`;
    };

    const handleLogout = async() => {
        try {
            await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/security_log/logout`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId: user?.id,
                        eventType: "logout",
                        eventResult: "success",
                        reason: "Logout via TopBar",
                    }),
                    credentials: "include",
                },
            );
            await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`,
                { method: "POST", credentials: "include" },
            );
        } catch(error) {
            console.error("Logout failed from Topbar:", error);
        }

        localStorage.clear();
        navigate("/login");
    };

    const renderProfileButton = (mobile = false) => (
        showProfilePicture && (
            <button
                type="button"
                onClick={() => goTo("/account")}
                title="Go to my profile"
                aria-label="Go to my profile"
                className={`aqi-profile-button ${mobile ? "h-9 w-9 text-sm" : "h-11 w-11 text-base"} flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-navy font-bold text-paper`}
            >
                {profileImage ? (
                    <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full object-cover"
                        onError={() => setProfileImage(null)}
                    />
                ) : (
                    getInitials(user)
                )}
            </button>
        )
    );

    const renderEducationLinks = (mobile = false) => (
        <ul className={mobile ? "space-y-2" : "space-y-3"}>
            {[
                ...(hasGeneralFAQ
                    ? [["General FAQ", "/faq/public/general"]]
                    : []),
                ...educationLinks,
            ].map(([label, path]) => (
                <li key={label} className="flex items-start">
                    <span className="mr-2 mt-1 text-gold">•</span>
                    <button
                        type="button"
                        onClick={() => goToPublic(path)}
                        className="w-full cursor-pointer border-none bg-transparent text-left text-navy/75 transition-colors hover:text-navy"
                    >
                        {label}
                    </button>
                </li>
            ))}
        </ul>
    );

    return (
        <div className="aqi-topbar font-body">
            <div className="bg-navy px-6 text-paper/80 md:px-12 lg:px-16">
                <div className="mx-auto flex w-full max-w-[1600px] items-center py-2 text-xs font-medium tracking-wide">
                    <span className="mr-3 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                    Securities offered through AQi are intended for investors
                    who can bear substantial risk. Investments are illiquid,
                    high risk, and you may lose your entire investment.
                </div>
            </div>
            <header className="relative z-40 w-full bg-paper px-6 py-4 font-body shadow-sm md:px-12 lg:px-16">
                <div className="mx-auto flex w-full max-w-[1600px] items-center justify-between">
                    <button
                        type="button"
                        onClick={() => goToPublic("/")}
                        className="flex cursor-pointer items-center border-none bg-transparent"
                        aria-label="AQi home"
                    >
                        <img
                            src="/AeQuitas-Logo.png"
                            alt="AQi Logo"
                            className="h-8 w-auto sm:h-9"
                        />
                    </button>

                    <nav className="ml-auto hidden flex-1 lg:flex">
                        <ul className="ml-auto flex items-center gap-6 text-navy">
                            {[
                                ["Mission", "/mission", "public"],
                                ["Investors", "/investors", "frontend"],
                                ["Entrepreneurs", "/entrepreneurs", "public"],
                                ["News", "/news", "public"],
                            ].map(([label, path, target]) => (
                                <li key={label}>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            target === "frontend"
                                                ? goToFrontend(path)
                                                : goToPublic(path)
                                        }
                                        className="cursor-pointer border-none bg-transparent font-body text-[15px] font-medium text-navy/75 transition-colors hover:text-navy"
                                    >
                                        {label}
                                    </button>
                                </li>
                            ))}
                            <li ref={educationRef} className="relative">
                                <button
                                    type="button"
                                    onClick={() => setShowEducationDropdown((open) => !open)}
                                    className="border-none bg-transparent font-body text-[15px] font-medium text-navy/75 transition-colors hover:text-navy focus:outline-none"
                                >
                                    Education
                                </button>
                                {showEducationDropdown && (
                                    <div className="absolute left-1/2 z-50 mt-3 w-64 -translate-x-1/2 rounded-xl border border-navy/10 bg-paper p-4 shadow-[0_18px_45px_rgba(11,31,58,0.12)]">
                                        {renderEducationLinks()}
                                    </div>
                                )}
                            </li>
                            <li>
                                <button type="button" onClick={() => goTo("/sign-up")} className="aqi-signup-button cursor-pointer rounded-[10px] border border-navy bg-navy px-5 py-2.5 font-body text-sm font-semibold text-paper shadow-sm transition-colors hover:bg-navy/90">
                                    Sign Up
                                </button>
                            </li>
                            {!user ? (
                                <li>
                                    <button type="button" onClick={() => goTo("/login")} className="cursor-pointer rounded-[10px] border border-navy/15 bg-paper px-4 py-2.5 font-body text-sm font-semibold text-navy transition-colors hover:border-gold hover:text-gold">
                                        Log In
                                    </button>
                                </li>
                            ) : (
                                <>
                                    {canAccessDashboard && (
                                        <li>
                                            <button type="button" onClick={() => goTo("/dashboard")} className="border-none bg-transparent text-[15px] font-medium text-navy/75 hover:text-navy">Dashboard</button>
                                        </li>
                                    )}
                                    <li>
                                        <button type="button" onClick={handleLogout} className="rounded-[10px] bg-gold px-4 py-2.5 text-sm font-bold text-navy">Log Out</button>
                                    </li>
                                    <li>{renderProfileButton()}</li>
                                </>
                            )}
                        </ul>
                    </nav>

                    <div className="flex items-center gap-2 lg:hidden">
                        {!user ? (
                            <button type="button" onClick={() => goTo("/login")} className="rounded-[8px] bg-gold px-3 py-2 text-sm font-bold text-navy">Log In</button>
                        ) : (
                            renderProfileButton(true)
                        )}
                        <button
                            ref={mobileMenuButtonRef}
                            type="button"
                            onClick={() => {
                                setIsMobileMenuOpen((open) => !open);
                                setShowMobileEducationDropdown(false);
                            }}
                            className="aqi-mobile-trigger p-2 text-navy focus:outline-none"
                            aria-label="Toggle mobile menu"
                            aria-expanded={isMobileMenuOpen}
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <div ref={mobileMenuRef} className="absolute left-0 right-0 top-full border-t border-navy/10 bg-paper px-6 py-5 shadow-lg lg:hidden">
                        <nav>
                            <ul className="space-y-2 text-navy">
                                {[
                                    ["Mission", "/mission", "public"],
                                    ["Investors", "/investors", "frontend"],
                                    ["Entrepreneurs", "/entrepreneurs", "public"],
                                    ["News", "/news", "public"],
                                ].map(([label, path, target]) => (
                                    <li key={label}>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                target === "frontend"
                                                    ? goToFrontend(path)
                                                    : goToPublic(path)
                                            }
                                            className="w-full border-none bg-transparent py-2 text-left font-medium text-navy/75 hover:text-navy"
                                        >
                                            {label}
                                        </button>
                                    </li>
                                ))}
                                <li>
                                    <button
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setShowMobileEducationDropdown(
                                                (open) => !open,
                                            );
                                        }}
                                        className="w-full border-none bg-transparent py-2 text-left font-medium text-navy/75 hover:text-navy"
                                    >
                                        Education
                                    </button>
                                    {showMobileEducationDropdown && (
                                        <div className="ml-3 mt-1 rounded-lg border border-navy/10 bg-white p-3">
                                            {renderEducationLinks(true)}
                                        </div>
                                    )}
                                </li>
                                <li><button type="button" onClick={() => goTo("/sign-up")} className="w-full border-none bg-transparent py-2 text-left font-medium text-navy/75 hover:text-navy">Sign Up</button></li>
                                {user && canAccessDashboard && <li><button type="button" onClick={() => goTo("/dashboard")} className="w-full border-none bg-transparent py-2 text-left font-medium text-navy/75 hover:text-navy">Dashboard</button></li>}
                                {user && <li><button type="button" onClick={handleLogout} className="w-full border-none bg-transparent py-2 text-left font-medium text-navy/75 hover:text-navy">Log Out</button></li>}
                            </ul>
                        </nav>
                    </div>
                )}
            </header>
        </div>
    );
};

export default TopBar;
